import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { TFiltersQ4Us } from 'redux/types';
import { loadEmVatCorrectionsImportListRequest } from 'redux/reducers/emVatCorrectionsImport/reducer';
import {
	EM_VAT_CORRECTIONS_IMPORT_VIEW_TABLE,
	EM_VAT_CORRECTIONS_NON_UNION_VIEW_TABLE,
	EM_VAT_CORRECTIONS_NON_EU_VIEW_TABLE,
	EM_VAT_CORRECTIONS_UNION_VIEW_TABLE,
	VAT_CORRECTIONS_CONSOLIDATED_VIEW_TABLE,
} from 'redux/reducers/emVatCorrectionsImport/constants';
import { TEmVatCorrectionsImportRow } from 'redux/reducers/emVatCorrectionsImport/types';
import {
	selectEmVatCorrectionsImportListLoading,
	selectEmVatCorrectionsImportListPage,
	selectEmVatCorrectionsImportListRowCount,
	selectEmVatCorrectionsImportListRows,
} from 'redux/reducers/emVatCorrectionsImport/selectors';
import { saveUrl } from 'redux/reducers/auth/reducer';
import { getEMerchantRequest } from 'redux/reducers/monthlyFee/reducer';
import { PATHS } from 'routes/constants';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import axios from 'axios';
import fileDownload from 'js-file-download';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import {
	CORRECTION_ACTION_BUTTON_NAME,
	DOWNLOAD_SUPPORTIVE_DOCUMENT_BUTTON_NAME,
} from './VerificationCorrectionActionDialog/constants';
import { VerificationCorrectionActionDialog } from './VerificationCorrectionActionDialog';
import { tableStructure } from './tableStructure';
import { getEmEnumsRequest } from '../../redux/reducers/emManagement/reducer';
import { EEmEnums } from '../../redux/reducers/emManagement/constants';

const resolveTableName = (navigateParams: queryString.ParsedQuery): string => {
	if (navigateParams['f-scheme'] === 'ioss') {
		return EM_VAT_CORRECTIONS_IMPORT_VIEW_TABLE;
	}
	if (navigateParams['f-scheme'] === 'non_union_ioss') {
		return EM_VAT_CORRECTIONS_NON_UNION_VIEW_TABLE;
	}
	if (navigateParams['f-scheme'] === 'non_union') {
		return EM_VAT_CORRECTIONS_NON_EU_VIEW_TABLE;
	}
	if (navigateParams['f-scheme'] === 'union') {
		return EM_VAT_CORRECTIONS_UNION_VIEW_TABLE;
	}
	return 'em_vat_corrections';
};

export const EmVatCorrections: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search, pathname } = useLocation();
	const navigateParams = queryString.parse(search);

	const [currentTableStructure, setCurrentTableStructure] = useState(tableStructure);
	const [actionDialogRow, setActionDialogRow] = useState<TEmVatCorrectionsImportRow | null>(null);

	const listRows = useSelector(selectEmVatCorrectionsImportListRows);
	const listLoading = useSelector(selectEmVatCorrectionsImportListLoading);
	const listPage = useSelector(selectEmVatCorrectionsImportListPage);

	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TEmVatCorrectionsImportRow>[]) => {
			setCurrentTableStructure(newTableStructure);
		},
		[],
	);

	const handleLink = useCallback(
		(emId: number) => {
			dispatch(saveUrl(window.location.pathname + window.location.search));
			navigate(generatePath(PATHS.EM_MANAGEMENT_ID_PAGE, { emId: String(emId) }));
		},
		[dispatch, navigate],
	);

	const pageSize = PAGE_SIZE;

	const resolvedTableName = useMemo(() => resolveTableName(queryString.parse(search)), [search]);

	const reloadList = useCallback(() => {
		const params = queryString.parse(search);
		const orderBy: string[] = [];
		if (params?.sortIndex) {
			const sortItem = tableStructure[+params.sortIndex];
			if (sortItem) {
				orderBy.push(
					`${String(sortItem.selector)} ${params?.sortDirection === 'desc' ? 'desc' : 'asc'}`,
				);
			}
		}
		const currentPage = Number(params.page) || 1;

		const navigateParamsWithoutPage = getFiltersFromNavigateParams(
			currentTableStructure,
			params,
		) as TFiltersQ4Us;

		if (params.search) {
			navigateParamsWithoutPage.push({
				name: 'full_name',
				value: `%${String(params.search)}%`,
				comparator: 'like',
			});
		}

		const tableName = resolveTableName(params);

		dispatch(
			loadEmVatCorrectionsImportListRequest({
				tableName: VAT_CORRECTIONS_CONSOLIDATED_VIEW_TABLE,
				orderBy: orderBy.length ? orderBy : ['id desc'],
				filter: navigateParamsWithoutPage,
				page: { size: pageSize, from: (currentPage - 1) * pageSize },
			}),
		);
	}, [currentTableStructure, dispatch, pageSize, search]);

	useEffect(() => {
		reloadList();
	}, [reloadList]);

	useEffect(() => {
		dispatch(getEMerchantRequest());
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SUPPORTED_CURRENCY }));
	}, [dispatch]);

	const currentPage = Number(navigateParams.page) || 1;

	const items = useMemo(() => {
		return {
			pageNumber: currentPage,
			pageSize: listPage?.size ?? pageSize,
			totalCount: listPage?.total ?? 0,
			data: listRows as TEmVatCorrectionsImportRow[],
		};
	}, [listPage, listRows, currentPage, pageSize]);

	// useEffect(() => {
	// 	if (!navigateParams['f-scheme']) {
	// 		navigate({
	// 			pathname,
	// 			search: queryString.stringify({
	// 				...navigateParams,
	// 				'f-scheme': 'ioss',
	// 			}),
	// 		});
	// 	}
	// }, [navigate, navigateParams, pathname]);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === CORRECTION_ACTION_BUTTON_NAME) {
				const row = listRows.find((r) => r.id === id);
				if (row) {
					setActionDialogRow(row);
				}
			}
			if (action === DOWNLOAD_SUPPORTIVE_DOCUMENT_BUTTON_NAME) {
				const row = listRows.find((r) => r.id === id);
				// const docId = row?.supportive_document;
				if (!row) {
					return;
				}
				const tableName = row.table_name ?? resolvedTableName;
				void (async () => {
					try {
						const blob = await api.emVatCorrectionsImport.fetchSupportiveDocument({
							id: row.id,
							tableName,
						});
						const fileName =
							row.supportive_document_name?.trim() || `supportive_document_${String(row.id)}`;
						fileDownload(blob, fileName);
					} catch (error) {
						if (axios.isAxiosError(error)) {
							const data = error.response?.data;
							let message: string | undefined =
								typeof data === 'object' &&
								data !== null &&
								'message' in data &&
								typeof (data as { message?: unknown }).message === 'string'
									? (data as { message: string }).message
									: undefined;
							if (message === undefined && data instanceof Blob) {
								try {
									const text = await data.text();
									const parsed = text ? (JSON.parse(text) as { message?: string }) : {};
									message = parsed?.message ?? text;
								} catch {
									message = 'Download failed';
								}
							}
							notificationContainer(String(message ?? error.message ?? 'Download failed'), 'error');
						}
					}
				})();
			}
		},
		[listRows, resolvedTableName],
	);

	const dialogTableName = actionDialogRow?.table_name ?? resolvedTableName;

	return (
		<Box sx={{ margin: '0px 10px 0 10px' }}>
			<VerificationCorrectionActionDialog
				open={Boolean(actionDialogRow)}
				row={actionDialogRow}
				tableName={dialogTableName}
				onClose={() => setActionDialogRow(null)}
				onSuccess={reloadList}
			/>
			<EasTableWithFilter<TEmVatCorrectionsImportRow>
				tableStructure={currentTableStructure}
				items={items}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				handleLink={handleLink}
				onTableStructureChange={handleTableStructureChange}
				emFilter
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary" fontWeight="bold">
						EM VAT Corrections
					</Typography>
				</Box>
			</EasTableWithFilter>
		</Box>
	);
};
